import { Icon } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import {
  AUDIO_TRANSCRIPT_FAILED_MESSAGE,
  AUDIO_TRANSCRIPT_READY_MESSAGE,
  EVENT_NAMES,
  WOUND_ANALYZER_PROCESSING_FAILED_MESSAGE,
  WOUND_ANALYZER_RESULT_READY_MESSAGE,
} from 'common/constants';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { preferTranscriptSession } from 'woundscribe/core/recordingStore';
import { shortDate } from 'styleguide/form/Date/formatters';
import { navigateToWoundAnalyzerResult } from 'utility/helpers';

const AccordionCard: React.FC<any> = ({ data, dataProps }) => {
  const navigate = useNavigate();
  const eventName = useMemo(() => {
    return data?.event?.title;
  }, [data]);

  const description = useMemo(() => {
    return data?.event?.description;
  }, [data]);

  const patient = useMemo(() => {
    return data?.meta?.patientName;
  }, [data]);

  const signedURL = useMemo(() => {
    return data?.requestDetails?.signedURL;
  }, [data]);

  const qrdaBulkURL = useMemo(() => {
    return data?.requestDetails?.qrdaBulkURL;
  }, [data]);
  const exportPatientFailed = useMemo(() => {
    return data?.requestDetails?.exportPatientFailed;
  }, [data]);
  const startDate = useMemo(() => {
    return data?.requestDetails?.startDate;
  }, [data]);
  const endDate = useMemo(() => {
    return data?.requestDetails?.endDate;
  }, [data]);

  const navigateToSection = useCallback(() => {
    dataProps?.accordionClick(data);
  }, [data, dataProps]);

  const handlePatientClick = useCallback(
    (e) => {
      e.stopPropagation();
      const redirect = `patients/${data.meta.patientId}`;
      if (redirect !== '') {
        navigate(`/${redirect}`);
        dataProps.onClose();
      }
    },
    [navigate, data, dataProps]
  );

  const handleWoundAnalyzerResultReadyClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!signedURL) return;
  
      dataProps?.onClose?.();
      navigateToWoundAnalyzerResult(signedURL);
    },
    [signedURL, dataProps]
  );

  const handleTranscriptClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!signedURL) return;
      const sessionId = data?.requestDetails?.sessionId;
      if (typeof sessionId === 'string' && sessionId) {
        preferTranscriptSession(sessionId);
      }
      dataProps?.onClose?.();
      navigate(`/${signedURL.replace(/^\//, '')}`);
    },
    [signedURL, data, dataProps, navigate]
  );

  return (
    <Box
      display={'flex'}
      flexDirection="column"
      flexGrow={1}
      onClick={navigateToSection}
      style={{
        cursor: 'pointer',
        padding: '8px',
        borderBottom: '1px solid #eee',
      }}
    >
      {/* Patient */}
      {eventName !== EVENT_NAMES.BULK_QRDA_EXPORT && (
        <Box display="flex">
          <Typography variant="body1" color="textSecondary">
            Patient:
          </Typography>
          <Box ml={0.5} onClick={handlePatientClick}>
            <Typography variant="body1" color="primary">
              {patient}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Patient Reference Number */}
      {data?.meta?.patientRefNo && (
        <Box display="flex" mt={0.5}>
          <Typography variant="body1" color="textSecondary">
            Ref No:
          </Typography>
          <Box ml={0.5}>
            <Typography variant="body1" color="primary">
              {data.meta.patientRefNo}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Event */}
      <Box display="flex" mt={0.5}>
        <Typography variant="body1" color="textSecondary">
          Event:
        </Typography>
        <Box ml={0.5}>
          <Typography variant="body1" color="primary">
            {description}
          </Typography>
        </Box>
      </Box>

      {/* EHI Export details */}
      <Box mt={2} p={2} bgcolor="#f9f9f9" borderRadius={4}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography
            variant="subtitle2"
            color="textSecondary"
            style={{ minWidth: 70 }}
          >
            Status:
          </Typography>
          {eventName === EVENT_NAMES.EHI_EXPORT && (
            <Typography variant="body1">Report Ready for Download</Typography>
          )}
          {eventName === EVENT_NAMES.WOUND_ANALYZER_RESULT_READY_EVENT_TITLE && (
            <Typography variant="body1">{WOUND_ANALYZER_RESULT_READY_MESSAGE}</Typography>
          )}
          {eventName === EVENT_NAMES.WOUND_ANALYZER_PROCESSING_FAILED_EVENT_TITLE && (
            <Typography variant="body1">{WOUND_ANALYZER_PROCESSING_FAILED_MESSAGE}</Typography>
          )}
          {eventName === EVENT_NAMES.AUDIO_TRANSCRIPT_READY_EVENT_TITLE && (
            <Typography variant="body1">{AUDIO_TRANSCRIPT_READY_MESSAGE}</Typography>
          )}
          {eventName === EVENT_NAMES.AUDIO_TRANSCRIPT_FAILED_EVENT_TITLE && (
            <Typography variant="body1" color="error">
              {AUDIO_TRANSCRIPT_FAILED_MESSAGE}
            </Typography>
          )}
          {eventName === EVENT_NAMES.EHI_EXPORT_FAILED && (
            <Typography variant="body1" color="error">
              Export failed. Please try again.
            </Typography>
          )}
          {eventName === EVENT_NAMES.BULK_QRDA_EXPORT && (
            <Typography variant="body1" color="primary">
              QRDA bulk export is ready for{' '}
              {shortDate(startDate, dataProps?.clinicianTimeZone)} to{' '}
              {shortDate(endDate, dataProps?.clinicianTimeZone)}.
            </Typography>
          )}
          {eventName === EVENT_NAMES.BULK_QRDA_EXPORT_FAILED && (
            <Typography variant="body1" color="error">
              {EVENT_NAMES.BULK_QRDA_EXPORT_FAILED}: {exportPatientFailed}
            </Typography>
          )}
        </Box>

        {eventName === EVENT_NAMES.EHI_EXPORT && (
          <Box display="flex" alignItems="center" mt={1}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              style={{ minWidth: 110 }}
            >
              Download Link:
            </Typography>
            <a
              href={signedURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon color={'primary'}>get_app</Icon>
              <Typography
                variant="body1"
                color="primary"
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Download Report
              </Typography>
            </a>
          </Box>
        )}
        {eventName === EVENT_NAMES.WOUND_ANALYZER_RESULT_READY_EVENT_TITLE && (
          <Box display="flex" alignItems="center" mt={1} onClick={handleWoundAnalyzerResultReadyClick}>
            <Typography variant="body1" color="primary">
              Click here to redirect to that Wound form
            </Typography>
          </Box>
        )}
        {eventName === EVENT_NAMES.AUDIO_TRANSCRIPT_READY_EVENT_TITLE && (
          <Box display="flex" alignItems="center" mt={1} onClick={handleTranscriptClick}>
            <Typography variant="body1" color="primary">
              Show transcript
            </Typography>
          </Box>
        )}
        {eventName === EVENT_NAMES.WOUND_ANALYZER_PROCESSING_FAILED_EVENT_TITLE && (
          <Box display="flex" alignItems="center" mt={1}>
            <Typography variant="body1" color="error">
              Please retake and upload a clear, well-lit, focused wound image and retry the analysis.
            </Typography>
          </Box>
        )}
        {eventName === EVENT_NAMES.BULK_QRDA_EXPORT && qrdaBulkURL && (
          <Box display="flex" alignItems="center" mt={1}>
            <Typography
              variant="subtitle2"
              color="textSecondary"
              style={{ minWidth: 110 }}
            >
              Download Link:
            </Typography>
            <a
              href={qrdaBulkURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon color={'primary'}>get_app</Icon>
              <Typography
                variant="body1"
                color="primary"
                style={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Download QRDA Bulk Export
              </Typography>
            </a>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(AccordionCard);
